<template>
  <div class="totals-cnt">
    <ul class="totals">
      <li>
        <p>{{ $t('viewOrder.total_items_in_order') }}</p>
        <span>{{ totalItems }}</span>
      </li>
      <li :class="{ clear: !showGrossTotal }">
        <p>{{ $t('viewOrder.order_total') }}</p>
        <span v-html="currencyFormatter(orderDetails.total ?? 0)"></span>
      </li>
      <li v-if="showGrossTotal" class="clear label">
        <p>{{ $t('viewOrder.order_gross_total_message') }}</p>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { DraftModel } from '@onrunning/backstage-models';
import { Order } from '../../models/order.model';
import { ValidatorResult } from '../../models/validation-result.model';
import { DraftsService } from '../../services/drafts.service';
import { storeToRefs } from 'pinia';
import { useUserStore } from '../../stores/user';
import { useFormat } from '../../composables/format';

export default defineComponent({
  name: 'view-order-totals',
  props: {
    order: {
      type: Object,
      default: undefined,
    },
  },
  setup() {
    const { selectedAccount } = storeToRefs(useUserStore());
    const { formatCurrency } = useFormat();

    return { account: selectedAccount, formatCurrency };
  },
  data() {
    return {
      draftsService: new DraftsService(),
      fetchingPrices: false,
      orderDetails: this.order as unknown as Order,
    };
  },
  computed: {
    totalItems(): number {
      return this.orderDetails.products.reduce(
        (total: number, product: { quantity: number }) => total + Math.round(product.quantity),
        0,
      );
    },
    showGrossTotal(): boolean {
      return this.orderDetails.total && this.orderDetails.total > 0 && !this.orderDetails.subTotal ? true : false;
    },
  },
  mounted() {
    if (this.orderDetails?.pricesAsync) {
      this.fetchingPrices = true;
      this.draftsService
        .getPrices(this.orderDetails?.id, this.account.id)
        .then((pricesResult: ValidatorResult<DraftModel>) => {
          if (pricesResult.model) {
            const orderWithPrices = pricesResult.model;

            this.orderDetails.total = orderWithPrices.total;
            this.orderDetails.subTotal = orderWithPrices.subTotal;

            this.orderDetails.products.forEach((product) => {
              const productWithPrice = orderWithPrices.products.find(
                (pricesResultProduct) => pricesResultProduct.productId === product.productId,
              );

              if (productWithPrice) {
                product.unitPrice = productWithPrice.unitPrice;

                if (productWithPrice.totalPrice) {
                  product.totalPrice = productWithPrice.totalPrice;
                }
              }
            });
          }
        })
        .finally(() => {
          this.fetchingPrices = false;
        });
    }
  },
  methods: {
    currencyFormatter(value: number) {
      if (this.orderDetails.pricesAsync && this.fetchingPrices) {
        return '<div class="grid-column-loader" />';
      }

      return this.formatCurrency(this.orderDetails?.currencyCode, value);
    },
  },
});
</script>
